import { getBusinessApiClient } from '@infra/api/BusinessApiClient';
import { RemoteSyncStore } from '@infra/sync/RemoteSyncStore';
import { logger } from '@infra/telemetry/Logger';
import { TenantContext } from '@infra/tenancy/TenantContext';
import type { Tenant } from '@shared/types/Tenant';
import { wrapModuleDataPayload } from '@application/persistence/moduleDataPayload';
import {
  ALL_REMOTE_RESOURCES,
  collectDossierNamespaces,
  hasMeaningfulDossierValue,
  isDossierNamespace,
} from '@application/persistence/RemoteParityCatalog';
import { ModuleDataSyncService } from '@application/persistence/ModuleDataSyncService';
import { isCloudWebPlaceholderTenant } from '@application/licence/CloudWebHost';

export interface CloudDossierHydration {
  pulled: number;
  pushed: number;
  namespaces: number;
}

/** Enregistrement module-data distant (list/get/upsert) — typage explicite des callbacks. */
interface RemoteModuleDataListItem {
  namespace: string;
  payload: Record<string, unknown>;
  version: number;
  tenant_id?: string;
}

/**
 * Place tout le dossier Cloud chez Nexxus : familles métier + tous les
 * namespaces (réglages, saisie, conformité, vie sociale, DTS, etc.).
 */
export class CloudDossierSyncService {
  static async hydrateEntireDossier(tenantId: string): Promise<CloudDossierHydration> {
    if (isCloudWebPlaceholderTenant(tenantId)) {
      return { pulled: 0, pushed: 0, namespaces: 0 };
    }
    const api = getBusinessApiClient();
    if (!(await api.confirmWriteReady()) || !api.hasUserSession()) {
      return { pulled: 0, pushed: 0, namespaces: 0 };
    }

    let pulled = 0;
    let pushed = 0;

    for (const resource of ALL_REMOTE_RESOURCES) {
      try {
        const rows = await api.listEntities(resource);
        const scoped = rows.filter((row) => !row.tenant_id || row.tenant_id === tenantId);
        if (scoped.length !== rows.length) {
          logger.warn({
            event: 'cloud_dossier_entity_tenant_mismatch',
            resource,
            requested: tenantId,
            dropped: rows.length - scoped.length,
          });
        }
        if (await RemoteSyncStore.isEnabled()) {
          for (const row of scoped) {
            await RemoteSyncStore.putCache(
              tenantId,
              resource,
              row.id,
              row.payload,
              row.version,
              false,
            );
          }
        }
        pulled += scoped.length;
      } catch (err) {
        logger.warn({
          event: 'cloud_dossier_resource_skip',
          resource,
          error: String(err),
        });
      }
    }

    try {
      const profile = await api.getTenantProfile();
      if (profile && profile.tenant_id !== tenantId) {
        logger.warn({
          event: 'cloud_dossier_profile_tenant_mismatch',
          requested: tenantId,
          received: profile.tenant_id,
        });
      } else if (profile) {
        if (await RemoteSyncStore.isEnabled()) {
          await RemoteSyncStore.putCache(
            tenantId,
            'tenant-profile',
            tenantId,
            profile.payload,
            profile.version,
            false,
          );
        }
        // Aligne le contexte mémoire avec le profil serveur (en-têtes docs / dashboard).
        const active = TenantContext.getInstance().tryGetTenant();
        if (active?.id === tenantId) {
          const remoteParametres =
            profile.payload['parametres'] && typeof profile.payload['parametres'] === 'object'
              ? (profile.payload['parametres'] as Tenant['parametres'])
              : undefined;
          TenantContext.getInstance().setTenant({
            ...active,
            ...(profile.payload as Partial<Tenant>),
            id: tenantId,
            parametres: { ...active.parametres, ...remoteParametres },
          });
        }
        pulled += 1;
      }
    } catch (err) {
      logger.warn({ event: 'cloud_dossier_profile_skip', error: String(err) });
    }

    const localRows = (await ModuleDataSyncService.listForTenant(tenantId)).filter((row) =>
      isDossierNamespace(row.namespace),
    );
    let listed: RemoteModuleDataListItem[] | null = null;
    try {
      listed = await api.listModuleData();
    } catch (err) {
      logger.warn({ event: 'cloud_dossier_module_list_skip', error: String(err) });
    }

    const remoteNamespaces = listed?.map((item: RemoteModuleDataListItem) => item.namespace) ?? [];
    const namespaces = collectDossierNamespaces(
      localRows.map((row) => row.namespace),
      remoteNamespaces,
    );
    const remoteByNs = new Map(
      (listed ?? []).map((item: RemoteModuleDataListItem) => [item.namespace, item]),
    );
    const listComplete = listed !== null;

    for (const namespace of namespaces) {
      try {
        const remote =
          remoteByNs.get(namespace) ?? (listComplete ? undefined : await api.getModuleData(namespace));
        if (remote) {
          await ModuleDataSyncService.ingestRemote(tenantId, remote);
          pulled += 1;
          continue;
        }
        const local = localRows.find((row) => row.namespace === namespace);
        if (!local || !hasMeaningfulDossierValue(local.value)) continue;
        const payload = wrapModuleDataPayload(local.value);
        const saved = await api.upsertModuleData(namespace, payload, 1);
        await ModuleDataSyncService.ingestRemote(tenantId, saved);
        pushed += 1;
      } catch (err) {
        logger.warn({
          event: 'cloud_dossier_namespace_skip',
          namespace,
          error: String(err),
        });
      }
    }

    logger.info({
      event: 'cloud_dossier_hydrated',
      tenantId,
      pulled,
      pushed,
      namespaces: namespaces.length,
    });
    return { pulled, pushed, namespaces: namespaces.length };
  }
}
